// Plugin for setting the context for the global LLM

"use strict";

import { App, Ref, watch, WatchHandle } from "vue";
import { globalLLmAddPageContext, globalLLmDeletePageContext, PageLLmContext } from "./control/llm-chat-assitant";
import { getUniqueStringId } from "./utils/unique-id";

declare module "@vue/runtime-core" {
    interface ComponentCustomProperties {
        /**
         * Unique ID for the LLM context
         */
        $globalLlmContextId: string;

        /**
         * Sets the LLM context for the component (will be cleared after unmounts).
         * You can call this multiple times to change the previous context.
         * @param context The context
         * @param priority The priority of the context (higher priority is put first)
         */
        $setLlmContext: (context: PageLLmContext, priority?: number) => void;

        /**
         * Clears the LLM context of the component
         */
        $clearLLmContext: () => void;

        /**
         * Timeout to update the LLM context without spamming the websocket
         */
        $llmContextUpdateDebounceTimer: null | ReturnType<typeof setTimeout>;

        /**
         * Sets the LLM context for the component (will be cleared after unmounts).
         * Pass a computed reference to update the context when the component related data updates.
         * This method avoids spamming the websocket by only updating a max of 2 times per second
         * @param context The context (computed ref)
         * @param priority The priority of the context (higher priority is put first)
         */
        $setDynamicLlmContext: (context: Ref<PageLLmContext>, priority?: number) => void;

        /**
         * Dynamic LLM context watcher
         */
        $dynLlmContextWatcher: WatchHandle | null;
    }
}

// Debounce delay for updating the LLM context
const LLM_CONTEXT_UPDATE_DEBOUNCE_DELAY = 500;

export const llmGlobalContextPlugin = {
    install: (app: App) => {
        app.mixin({
            methods: {
                $setLlmContext: function (context: PageLLmContext, priority?: number) {
                    if (!this.$globalLlmContextId) {
                        this.$globalLlmContextId = getUniqueStringId();
                    }
                    globalLLmAddPageContext(this.$globalLlmContextId, context, priority);
                },
                $clearLLmContext: function () {
                    if (!this.$globalLlmContextId) {
                        return;
                    }

                    globalLLmDeletePageContext(this.$globalLlmContextId);
                },

                $setDynamicLlmContext: function (context: Ref<PageLLmContext>, priority?: number) {
                    if (this.$dynLlmContextWatcher) {
                        this.$dynLlmContextWatcher.stop();
                        this.$dynLlmContextWatcher = null;
                    }

                    this.$setLlmContext(context.value, priority);

                    this.$dynLlmContextWatcher = watch(context, () => {
                        if (this.$llmContextUpdateDebounceTimer) {
                            return;
                        }

                        this.$llmContextUpdateDebounceTimer = setTimeout(() => {
                            this.$llmContextUpdateDebounceTimer = null;

                            this.$setLlmContext(context.value, priority);
                        }, LLM_CONTEXT_UPDATE_DEBOUNCE_DELAY);
                    });
                },
            },
            beforeUnmount() {
                if (this.$llmContextUpdateDebounceTimer) {
                    clearTimeout(this.$llmContextUpdateDebounceTimer);
                    this.$llmContextUpdateDebounceTimer = null;
                }
                if (this.$dynLlmContextWatcher) {
                    this.$dynLlmContextWatcher.stop();
                    this.$dynLlmContextWatcher = null;
                }
                this.$clearLLmContext();
            },
        });
    },
};
